/**
 * @file FontsTab.tsx
 * @description Вкладка «Шрифты» оформления сайта (phase19.16 Р1, Р1-7; SB10,
 * SB73, SB99; uiux-design-r1 §1; SB10 дополнение 2026-10-11 —
 * uiux-fonts-usage §3, §6): три роли — «Заголовки», «Основной текст»,
 * «Акцент · необязательно»; у каждой назначение, карточка шрифта, строка
 * «Где: …» и список «Все места · N» из реестра `FONT_ROLES`
 * (@chaiberi/shared). Под заголовками — начертание и регистр, под основным
 * текстом — шрифт надписей капсом. Размеры и интервалы задаёт платформа.
 * Загрузка своего шрифта — одним входом через окно «Сменить».
 *
 * SB122 (uiux-settings-groups §5 шаг 3): роли — группы-карточки, панель
 * `fonts`, id группы = ключ роли (`heading`, `body`, `accent`). Адрес из
 * подсказки элемента: `?font=<роль>` → общий `OpenGroupRequest` (раскрыть,
 * прокрутить, подсветить; ключ — запись истории, повторная ссылка снова
 * прокручивает) и раскрытый список «Все места»; `?group=<роль>`
 * работает так же (корень групп). `?back=…&backLabel=…` — ссылка «← К
 * элементу «…»» в общей липкой строке панели (`stickyTop`,
 * `lib/site-view/fonts-tab-params.ts`).
 */

import { EYEBROW_FROM_NOTE, FONT_ROLE_KEYS, FONT_ROLES, effectiveFontKey, type FontRole, type SiteTheme } from "@chaiberi/shared";
import { useMemo, useState, type ReactNode } from "react";
import { useLocation, useSearchParams } from "react-router";

import { OpenGroupRequest, SettingsGroups } from "../../ui/settings-groups";
import { useShopFonts } from "../shop-fonts-context";
import { FONT_SAMPLE_TEXT, FontCatalogDialog } from "./FontCatalogDialog";
import { FontRoleCard } from "./FontRoleCard";
import { EyebrowFromField, HeadingTypeFields } from "./FontRoleFields";
import { FontRoleSection } from "./FontRoleSection";
import { FontsBackLink } from "./FontsBackLink";
import { headingWeightChoice } from "../../../lib/site-view/font-weights";
import { fontRoleSummary } from "../../../lib/site-view/font-role-summary";
import { fontDialogTitle, parseFontsTabParams } from "../../../lib/site-view/fonts-tab-params";
import { useThemePreview } from "../../../hooks/site-view/useThemePreview";
import { HEADING_SCALE_RECOMMENDED, outOfRecommended, TRACKING_RECOMMENDED } from "../../../lib/site-view/appearance-numbers";

/** Образец и фильтр окна каталога по роли; заголовок окна — «Шрифт <роли>» из реестра. */
const ROLE_VIEW: Readonly<Record<FontRole, { sample: string; size: number; category: "all" | "script" }>> = {
  heading: { sample: "Новая коллекция", size: 24, category: "all" },
  body: { sample: `${FONT_SAMPLE_TEXT}.`, size: 16, category: "all" },
  // Акцент открывает каталог с фильтром «Рукописные» — лучше всего подходят они.
  accent: { sample: "Сделано с любовью", size: 22, category: "script" },
};

interface Props {
  theme: SiteTheme;
  onFonts: (patch: Partial<SiteTheme["fonts"]>) => void;
  onType: (patch: Partial<SiteTheme["type"]>) => void;
  /** Открыть раздел «Дизайны» (из окна «Шрифт используется»). */
  onOpenDesigns?: () => void;
}

export function FontsTab({ theme, onFonts, onType, onOpenDesigns }: Props) {
  const [editing, setEditing] = useState<FontRole | null>(null);
  const [params] = useSearchParams();
  const { focus, back } = useMemo(() => parseFontsTabParams(params), [params]);
  // Один раз на переход: повторная ссылка на ту же роль из другого элемента снова прокручивает к ней.
  const { key: locationKey } = useLocation();
  const { registry } = useShopFonts();
  // Ползунки размера и разрядки — живьём на канве до отпускания (SB120 этап 2).
  const preview = useThemePreview(theme);
  const weight = headingWeightChoice(theme.fonts.heading, theme.type.headingWeight, registry);

  // «!» у роли за числа вне рекомендуемого — те же правила, что под полями (SB120 этап 2, SB122).
  const numberWarning: Readonly<Record<FontRole, boolean>> = {
    heading: outOfRecommended(theme.type.headingScale, HEADING_SCALE_RECOMMENDED),
    body: outOfRecommended(theme.type.eyebrowTracking, TRACKING_RECOMMENDED),
    accent: false,
  };

  // Настройки под ролью — реестром, а не ветками в разметке.
  const extras: Readonly<Record<FontRole, ReactNode>> = {
    heading: (
      <HeadingTypeFields weight={weight} headingCase={theme.type.headingCase} headingScale={theme.type.headingScale} onType={onType} onPreview={(patch) => preview("type", patch)} />
    ),
    body: (
      <EyebrowFromField
        value={theme.type.eyebrowFrom}
        tracking={theme.type.eyebrowTracking}
        fontKey={theme.type.eyebrowFrom === "heading" ? theme.fonts.heading : theme.fonts.body}
        onType={onType}
        onPreview={(patch) => preview("type", patch)}
      />
    ),
    accent: null,
  };

  return (
    <SettingsGroups panel="fonts" stickyTop={back ? <FontsBackLink back={back} /> : undefined}>
      <p className="type-body text-on-surface-variant">На сайте три шрифта. Под каждым — где он стоит.</p>
      {FONT_ROLE_KEYS.map((role) => {
        const view = ROLE_VIEW[role];
        const effective = effectiveFontKey(role, theme);
        const fallback = effective.fallback ? { role: effective.role, fontKey: effective.key } : undefined;
        const ownsEyebrow = role === "heading" && theme.type.eyebrowFrom === "heading";
        const summary = fontRoleSummary(role, theme, registry, weight.value);
        return (
          <FontRoleSection
            key={role}
            role={role}
            focused={focus === role}
            summary={summary.summary}
            status={summary.warning || numberWarning[role] ? "warning" : undefined}
            fallbackRole={fallback?.role ?? null}
            note={ownsEyebrow ? EYEBROW_FROM_NOTE : null}
            card={
              <FontRoleCard
                fontKey={theme.fonts[role]}
                sample={view.sample}
                sampleSize={view.size}
                weight={role === "heading" ? weight.value : undefined}
                fallback={fallback}
                onChange={() => setEditing(role)}
                onRemove={FONT_ROLES[role].optional ? () => onFonts({ [role]: null }) : undefined}
              />
            }
          >
            {extras[role]}
          </FontRoleSection>
        );
      })}
      <OpenGroupRequest id={focus} token={focus ? locationKey : null} />
      {/* Вкладка открыта только с правом правки THEME = праву на свои шрифты; подпись без права — в окне каталога. */}
      <p className="type-caption text-on-surface-variant">
        Свой шрифт можно загрузить в окне «Сменить». Размеры и интервалы задаёт платформа: текст на телефоне не мельче
        16px.
      </p>
      <FontCatalogDialog
        open={editing !== null}
        title={editing ? fontDialogTitle(editing) : ""}
        current={editing ? theme.fonts[editing] : null}
        draftFonts={theme.fonts}
        initialCategory={editing ? ROLE_VIEW[editing].category : "all"}
        onClose={() => setEditing(null)}
        onSelect={(key) => {
          if (editing) onFonts({ [editing]: key });
          setEditing(null);
        }}
        onOpenDesigns={onOpenDesigns}
      />
    </SettingsGroups>
  );
}
